import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { listSpendingTransactions } from "../../lib/api-modules/spending-transactions.js";
import { listIncomeEvents } from "../../lib/api-modules/income-events.js";
import { listBudgetCategories } from "../../lib/api-modules/budget-categories.js";
import {
  evaluateLifestyleInflation,
  type CategoryYearlySpend,
} from "../../lib/util/lifestyle-inflation.js";
import { Card, Pill } from "../design-system/index.js";
import { formatCentsCompact } from "../../lib/util/money.js";

/**
 * Phase 13 — Lifestyle inflation dashboard tile.
 *
 * Renders only when at least one category's YoY growth exceeds the
 * household income growth + threshold. Composes existing spending +
 * income + budget-category list endpoints; runs the pure helper
 * client-side (no new API endpoint).
 *
 * Phase 13 lock: tile is "absent when nothing flagged" by design — a
 * permanent-but-empty tile would be noise. The monthly cron handles
 * the email path; this tile catches anyone who dashboards before the
 * 15th-of-month nudge fires.
 */

const RECURRING_INCOME_TYPES = new Set([
  "salary_paycheck",
  "bonus",
  "side_income",
  "espp_purchase",
]);

interface Props {
  readonly today: string; // YYYY-MM-DD
  readonly warnPp?: number;
}

export function LifestyleInflationTile({ today, warnPp }: Props) {
  const minus = (days: number): string => {
    const d = new Date(`${today}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - days);
    return d.toISOString().slice(0, 10);
  };
  const currentTtmStart = minus(365);
  const priorTtmStart = minus(730);
  const priorTtmEnd = minus(366);

  const spendQ = useQuery({
    queryKey: ["lifestyle-inflation", "spending", priorTtmStart, today],
    queryFn: () =>
      listSpendingTransactions({ from: priorTtmStart, to: today }),
  });
  const incomeQ = useQuery({
    queryKey: ["lifestyle-inflation", "income", priorTtmStart, today],
    queryFn: () => listIncomeEvents({ from: priorTtmStart, to: today }),
  });
  const categoriesQ = useQuery({
    queryKey: ["lifestyle-inflation", "categories"],
    queryFn: () => listBudgetCategories(true),
  });

  const result = useMemo(() => {
    if (!spendQ.data || !incomeQ.data || !categoriesQ.data) return null;
    const categoryMap = new Map(
      categoriesQ.data.budget_categories.map((c) => [c.category_id, c.name]),
    );
    const accum = new Map<
      string,
      { current_ttm_cents: number; prior_ttm_cents: number }
    >();
    for (const r of spendQ.data.spending_transactions) {
      if (typeof r.amount_cents !== "number" || r.amount_cents <= 0) continue;
      const label = r.category_id
        ? (categoryMap.get(r.category_id) ?? r.category_id)
        : "(uncategorized)";
      const slot = accum.get(label) ?? {
        current_ttm_cents: 0,
        prior_ttm_cents: 0,
      };
      if (r.date >= currentTtmStart && r.date <= today) {
        slot.current_ttm_cents += r.amount_cents;
      } else if (r.date >= priorTtmStart && r.date <= priorTtmEnd) {
        slot.prior_ttm_cents += r.amount_cents;
      }
      accum.set(label, slot);
    }
    const byCategory: CategoryYearlySpend[] = Array.from(
      accum.entries(),
    ).map(([label, slot]) => ({
      category_label: label,
      current_ttm_cents: slot.current_ttm_cents,
      prior_ttm_cents: slot.prior_ttm_cents,
    }));

    let currentIncome = 0;
    let priorIncome = 0;
    for (const r of incomeQ.data.income_events) {
      if (r.is_archived) continue;
      if (!RECURRING_INCOME_TYPES.has(r.event_type)) continue;
      if (typeof r.amount_cents !== "number" || r.amount_cents <= 0) continue;
      if (r.date >= currentTtmStart && r.date <= today)
        currentIncome += r.amount_cents;
      else if (r.date >= priorTtmStart && r.date <= priorTtmEnd)
        priorIncome += r.amount_cents;
    }
    return evaluateLifestyleInflation({
      by_category: byCategory,
      current_ttm_income_cents: currentIncome,
      prior_ttm_income_cents: priorIncome,
      ...(warnPp !== undefined ? { warn_pp: warnPp } : {}),
    });
  }, [
    spendQ.data,
    incomeQ.data,
    categoriesQ.data,
    currentTtmStart,
    priorTtmStart,
    priorTtmEnd,
    today,
    warnPp,
  ]);

  // Hide the tile entirely while loading or when nothing flagged.
  if (
    spendQ.isLoading ||
    incomeQ.isLoading ||
    categoriesQ.isLoading ||
    !result
  ) {
    return null;
  }
  if (result.flagged_count === 0) return null;

  return (
    <Card>
      <div className="mb-3 flex items-start justify-between gap-3">
        <h3 className="font-display text-lg font-medium text-[var(--color-text)]">
          Lifestyle inflation
        </h3>
        <Pill tone="warning">{result.flagged_count} flagged</Pill>
      </div>
      <p className="mb-3 text-sm text-[var(--color-muted)]">
        Income grew{" "}
        <strong className="text-[var(--color-text)]">
          {result.income_growth_pct.toFixed(1)}%
        </strong>{" "}
        YoY. These categories grew faster than your earning power; if you'd
        held them to the threshold, you'd have spent roughly{" "}
        <strong className="text-[var(--color-text)]">
          {formatCentsCompact(result.creep_above_threshold_cents)}
        </strong>{" "}
        less.
      </p>
      <ul className="space-y-2">
        {result.rows
          .filter((r) => r.flagged)
          .slice(0, 5)
          .map((r) => (
            <li
              key={r.category_label}
              className="flex items-baseline justify-between gap-3 text-sm"
            >
              <span className="truncate text-[var(--color-text)]">
                {r.category_label}
              </span>
              <span className="shrink-0 font-mono text-xs text-[var(--color-muted)]">
                {formatCentsCompact(r.current_ttm_cents)} (+
                {Number.isFinite(r.growth_pct)
                  ? r.growth_pct.toFixed(0)
                  : "∞"}
                %)
              </span>
            </li>
          ))}
      </ul>
      <p className="mt-3 text-xs text-[var(--color-muted)]">
        Most growth is benign; ask whether each category crept up on
        purpose or by default.
      </p>
    </Card>
  );
}
